import React from 'react';
import { Modal as RNModal, ModalProps as RNModalProps, StyleSheet, Pressable, View, Platform } from 'react-native';

import { useTheme } from '@/components/theme-provider';
import { useSubmitShortcut } from '@/hooks/use-submit-shortcut';

export interface ModalProps extends Omit<RNModalProps, 'visible'> {
	visible: boolean;
	onDismiss?: () => void;
	children: React.ReactNode;
	contentContainerStyle?: any;
	/**
	 * When true, tapping the dimmed background invokes onDismiss. Defaults to
	 * false project-wide because a misclick on the backdrop wipes a half-
	 * filled form — a frequent annoyance during rapid data entry. Forms that
	 * want it can opt in (e.g., simple confirmation dialogs with no fields).
	 */
	dismissOnBackdrop?: boolean;
	/**
	 * Optional submit handler. When provided, pressing Cmd+Enter (Mac) or
	 * Alt/Ctrl+Enter (Windows/Linux) while this modal is visible fires it.
	 * Pass the same function you wire to your primary "Save" button.
	 */
	onSubmit?: () => void;
}

export const Modal: React.FC<ModalProps> = ({
	visible,
	onDismiss,
	children,
	contentContainerStyle,
	dismissOnBackdrop = false,
	onSubmit,
	...props
}) => {
	const theme = useTheme();

	useSubmitShortcut({ enabled: visible && !!onSubmit, onSubmit: onSubmit ?? (() => {}) });

	// Backdrop dismiss is opt-in. RN's hardware back button still calls
	// onRequestClose so the user can always escape.
	const allowOverlayDismiss = dismissOnBackdrop && Platform.OS === 'web';

	return (
		<RNModal
			visible={visible}
			transparent
			animationType="fade"
			onRequestClose={onDismiss}
			supportedOrientations={['portrait', 'landscape']}
			{...props}
		>
			<View style={styles.overlay}>
				{allowOverlayDismiss && (
					<Pressable
						style={StyleSheet.absoluteFill}
						onPress={onDismiss}
					/>
				)}
				<View
					style={[
						styles.content,
						{ backgroundColor: theme.colors.surface },
						contentContainerStyle,
					]}
				>
					{children}
				</View>
			</View>
		</RNModal>
	);
};

const styles = StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		justifyContent: 'center',
		alignItems: 'center',
	},
	content: {
		margin: 20,
		borderRadius: 8,
		padding: 20,
		minWidth: 300,
		maxWidth: '90%',
		maxHeight: '90%',
	},
});
